const API_URL = process.env.WORDPRESS_API_URL

const LOCALE_TO_WP_LANGUAGE: Record<string, string> = {
  en: 'EN',
  es: 'ES',
  ja: 'JA',
  ko: 'KO',
};

const POSTS_BATCH_SIZE = 100;
const MAX_PAGINATION_PAGES = 30;

/** Max concurrent WPGraphQL requests during build (prevents ECONNRESET under load). */
const WP_FETCH_CONCURRENCY = Math.max(
  1,
  Number(process.env.WP_FETCH_CONCURRENCY || 4)
);
const WP_FETCH_MAX_ATTEMPTS = Math.max(
  1,
  Number(process.env.WP_FETCH_MAX_ATTEMPTS || 5)
);
const WP_FETCH_TIMEOUT_MS = Math.max(
  5000,
  Number(process.env.WP_FETCH_TIMEOUT_MS || 60000)
);

const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
const RETRYABLE_NETWORK_CODES = new Set([
  'ECONNRESET',
  'ETIMEDOUT',
  'ECONNREFUSED',
  'ENOTFOUND',
  'EAI_AGAIN',
  'EPIPE',
  'UND_ERR_SOCKET',
  'UND_ERR_CONNECT_TIMEOUT',
  'UND_ERR_HEADERS_TIMEOUT',
  'UND_ERR_BODY_TIMEOUT',
  'ABORT_ERR',
]);

let wpFetchActive = 0;
const wpFetchWaitQueue: Array<() => void> = [];

async function withWpFetchLimit<T>(fn: () => Promise<T>): Promise<T> {
  if (wpFetchActive >= WP_FETCH_CONCURRENCY) {
    await new Promise<void>((resolve) => wpFetchWaitQueue.push(resolve));
  }
  wpFetchActive++;
  try {
    return await fn();
  } finally {
    wpFetchActive--;
    const next = wpFetchWaitQueue.shift();
    if (next) next();
  }
}

function wpFetchDebug(...args: unknown[]) {
  if (process.env.WP_FETCH_DEBUG === 'true') {
    console.log(...args)
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function retryDelayMs(attempt: number) {
  // Exponential backoff with jitter: ~0.5s, 1s, 2s, 4s...
  const base = Math.min(8000, 500 * 2 ** (attempt - 1));
  return base + Math.floor(Math.random() * 250);
}

function isRetryableNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string; cause?: { code?: string }; message?: string; name?: string };
  const code = err.code || err.cause?.code;
  if (code && RETRYABLE_NETWORK_CODES.has(code)) return true;
  if (err.name === 'AbortError' || err.name === 'TimeoutError') return true;
  const message = String(err.message || '');
  return /terminated|fetch failed|network|socket|ECONNRESET|ETIMEDOUT|aborted|other side closed/i.test(
    message
  );
}

function isRetryableGraphqlErrors(errors: any[]): boolean {
  const joined = errors.map((e) => String(e?.message || '')).join('; ');
  // Permanent client/query problems — never retry these.
  if (
    /syntax error|cannot query field|unknown argument|unknown type|variable .+ of type|validation/i.test(
      joined
    )
  ) {
    return false;
  }
  return /internal server error|timeout|too many|rate limit|temporarily unavailable|connection/i.test(
    joined
  );
}

function isPermanentGraphqlFailureBody(body: string) {
  return /syntax error|cannot query field|unknown argument|unknown type|validation/i.test(
    body
  );
}

/** In-process memo for identical WP queries during a single Next.js build. */
const buildQueryCache = new Map<string, Promise<any>>();

function isProductionBuildPhase() {
  return (
    process.env.NEXT_PHASE === 'phase-production-build' ||
    process.env.NEXT_PHASE === 'phase-export'
  );
}

function memoizeDuringBuild<T>(key: string, factory: () => Promise<T>): Promise<T> {
  if (!isProductionBuildPhase()) {
    return factory();
  }
  const existing = buildQueryCache.get(key);
  if (existing) return existing as Promise<T>;

  const pending = factory().catch((error) => {
    buildQueryCache.delete(key);
    throw error;
  });
  buildQueryCache.set(key, pending);
  return pending;
}

function shouldAttachWordpressAuth(requireAuth: boolean) {
  if (!process.env.WORDPRESS_AUTH_REFRESH_TOKEN) return false;
  // This WPGraphQL setup restricts fields like RootQuery.pages without auth,
  // so attach the token whenever it is configured. Preview can also force it.
  if (requireAuth) return true;
  if (process.env.WORDPRESS_AUTH_ON_BUILD === 'false') return false;
  return true;
}

function findMatchingBracketIndex(input: string, startIndex: number, openChar: string, closeChar: string) {
  let depth = 0;
  for (let i = startIndex; i < input.length; i++) {
    const ch = input[i];
    if (ch === openChar) depth++;
    if (ch === closeChar) {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function braceDepthAt(input: string, index: number) {
  let depth = 0;
  for (let i = 0; i < index; i++) {
    const ch = input[i];
    if (ch === '{') depth++;
    else if (ch === '}') depth--;
  }
  return depth;
}

function injectAfterVariableDefinition(query: string) {
  if (/\$after\s*:/.test(query)) return query;

  const queryKeywordMatch = query.match(/query\s*(?:[A-Za-z0-9_]+)?\s*(\([^)]*\))?\s*\{/m);
  if (queryKeywordMatch) {
    const matched = queryKeywordMatch[0];
    const existingVars = queryKeywordMatch[1];
    let updated = matched;

    if (existingVars) {
      // Keep $after inside the variable list: ($slug: String!, $after: String)
      const varsBody = existingVars.slice(1, -1).trim();
      updated = matched.replace(
        existingVars,
        varsBody ? `(${varsBody}, $after: String)` : `($after: String)`
      );
    } else {
      updated = matched.replace('{', '($after: String) {');
    }

    return query.replace(matched, updated);
  }

  return `query AutoPaginatedPosts($after: String) ${query.trim()}`;
}

function preparePostsPaginationQuery(query: string) {
  if (!/\bposts\s*\(/.test(query)) {
    return null;
  }

  const postsRegex = /posts\s*\(/g;
  let match: RegExpExecArray | null = null;

  while ((match = postsRegex.exec(query)) !== null) {
    // Only paginate root-level posts(...) — nested connections (e.g. tag.posts)
    // are not merged by fetchAPI and must not get $after rewritten.
    if (braceDepthAt(query, match.index) !== 1) continue;

    const openParenIndex = query.indexOf('(', match.index);
    if (openParenIndex < 0) continue;

    const closeParenIndex = findMatchingBracketIndex(query, openParenIndex, '(', ')');
    if (closeParenIndex < 0) continue;

    const argsText = query.slice(openParenIndex + 1, closeParenIndex);
    const firstMatch = argsText.match(/\bfirst\s*:\s*(\d+)\b/);
    if (!firstMatch) continue;

    const requestedFirst = Number(firstMatch[1]);
    if (requestedFirst <= POSTS_BATCH_SIZE) continue;

    let updatedArgs = argsText.replace(/\bfirst\s*:\s*\d+\b/, `first: ${POSTS_BATCH_SIZE}`);
    if (!/\bafter\s*:/.test(updatedArgs)) {
      updatedArgs = `${updatedArgs.trimEnd()}, after: $after`;
    }

    let updatedQuery = `${query.slice(0, openParenIndex + 1)}${updatedArgs}${query.slice(closeParenIndex)}`;

    const argsDelta = updatedArgs.length - argsText.length;
    const selectionOpenIndex = updatedQuery.indexOf('{', closeParenIndex + argsDelta);
    if (selectionOpenIndex < 0) return null;

    const selectionCloseIndex = findMatchingBracketIndex(updatedQuery, selectionOpenIndex, '{', '}');
    if (selectionCloseIndex < 0) return null;

    const selectionBody = updatedQuery.slice(selectionOpenIndex + 1, selectionCloseIndex);
    let didInjectPageInfo = false;

    if (!/\bpageInfo\b/.test(selectionBody)) {
      const pageInfoFragment = `
        pageInfo {
          hasNextPage
          endCursor
        }`;
      const updatedSelectionBody = `${pageInfoFragment}${selectionBody}`;
      updatedQuery = `${updatedQuery.slice(0, selectionOpenIndex + 1)}${updatedSelectionBody}${updatedQuery.slice(selectionCloseIndex)}`;
      didInjectPageInfo = true;
    }

    updatedQuery = injectAfterVariableDefinition(updatedQuery);

    return {
      query: updatedQuery,
      didInjectPageInfo,
    };
  }

  return null;
}


async function fetchAPI(
  query = '',
  {
    variables,
    locale = 'en',
    requireAuth = false,
  }: Record<string, any> = {}
) {
  const normalizedLocale = String(locale || 'en').toLowerCase()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*',
    'User-Agent': 'mw-website-nextjs-build',
  }

  if (shouldAttachWordpressAuth(Boolean(requireAuth))) {
    headers.Authorization = `Bearer ${process.env.WORDPRESS_AUTH_REFRESH_TOKEN}`
  }

  // Add language header for WordPress multilingual support (WPML/Polylang)
  if (normalizedLocale) {
    headers['Accept-Language'] = normalizedLocale
    headers['X-Language'] = normalizedLocale
  }

  const executeRequest = async (
    requestQuery: string,
    requestVariables?: Record<string, any>
  ) => {
    let lastError: unknown

    for (let attempt = 1; attempt <= WP_FETCH_MAX_ATTEMPTS; attempt++) {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), WP_FETCH_TIMEOUT_MS)

      try {
        const res = await withWpFetchLimit(() =>
          fetch(API_URL as string, {
            headers,
            method: 'POST',
            signal: controller.signal,
            body: JSON.stringify({
              query: requestQuery,
              variables: {
                ...(requestVariables || {}),
                ...(normalizedLocale && {
                  language: normalizedLocale.toUpperCase(),
                }),
              },
            }),
          })
        )

        if (!res.ok) {
          const errorText = await res.text()
          console.error(
            `API request failed with status ${res.status}:`,
            errorText.substring(0, 200)
          )
          if (
            RETRYABLE_STATUS.has(res.status) &&
            attempt < WP_FETCH_MAX_ATTEMPTS &&
            !isPermanentGraphqlFailureBody(errorText)
          ) {
            const delay = retryDelayMs(attempt)
            console.warn(
              `Retrying API request (${attempt}/${WP_FETCH_MAX_ATTEMPTS}) after ${delay}ms [HTTP ${res.status}]`
            )
            await sleep(delay)
            continue
          }
          throw new Error(`Failed to fetch API: ${res.status} ${res.statusText}`)
        }

        const contentType = res.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          const errorText = await res.text()
          console.error('API returned non-JSON response:', errorText.substring(0, 200))
          if (attempt < WP_FETCH_MAX_ATTEMPTS) {
            const delay = retryDelayMs(attempt)
            console.warn(
              `Retrying after non-JSON response (${attempt}/${WP_FETCH_MAX_ATTEMPTS}) in ${delay}ms`
            )
            await sleep(delay)
            continue
          }
          throw new Error('API returned non-JSON response')
        }

        const json = await res.json()
        if (json.errors) {
          console.error('GraphQL errors:', json.errors)
          const errorMessages = json.errors
            .map((err: any) => err.message || 'Unknown error')
            .join('; ')
          if (
            isRetryableGraphqlErrors(json.errors) &&
            attempt < WP_FETCH_MAX_ATTEMPTS
          ) {
            const delay = retryDelayMs(attempt)
            console.warn(
              `Retrying after GraphQL transient error (${attempt}/${WP_FETCH_MAX_ATTEMPTS}) in ${delay}ms`
            )
            await sleep(delay)
            continue
          }
          throw new Error(`Failed to fetch API: ${errorMessages}`)
        }

        return json.data
      } catch (error) {
        lastError = error
        const canRetry =
          attempt < WP_FETCH_MAX_ATTEMPTS && isRetryableNetworkError(error)
        if (!canRetry) {
          throw error instanceof Error
            ? error
            : new Error(`Failed to fetch API: ${String(error)}`)
        }
        const delay = retryDelayMs(attempt)
        const code =
          (error as any)?.cause?.code ||
          (error as any)?.code ||
          (error as any)?.name ||
          'network'
        console.warn(
          `Retrying API request (${attempt}/${WP_FETCH_MAX_ATTEMPTS}) after ${delay}ms [${code}]`
        )
        await sleep(delay)
      } finally {
        clearTimeout(timeoutId)
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error('Failed to fetch API after retries')
  }

  const paginationConfig = preparePostsPaginationQuery(query)
  if (!paginationConfig) {
    return executeRequest(query, variables)
  }

  if (process.env.WP_FETCH_DEBUG === 'true') {
    wpFetchDebug('Large posts query detected, fetching in paginated batches of 100')
  }

  const mergedEdges: any[] = []
  let after: string | null = null
  let hasNextPage = true
  let pageCount = 0
  let mergedData: any = null

  while (hasNextPage) {
    pageCount++
    if (pageCount > MAX_PAGINATION_PAGES) {
      throw new Error(
        `Aborted posts pagination after ${MAX_PAGINATION_PAGES} pages to avoid infinite loop`
      )
    }

    const pageVariables = {
      ...(variables || {}),
      after,
    }

    const pageData = await executeRequest(paginationConfig.query, pageVariables)
    if (!mergedData) {
      mergedData = pageData
    }

    const pageEdges = pageData?.posts?.edges || []
    mergedEdges.push(...pageEdges)

    const pageInfo = pageData?.posts?.pageInfo
    hasNextPage = Boolean(pageInfo?.hasNextPage)
    after = pageInfo?.endCursor || null
  }

  if (!mergedData?.posts) {
    return mergedData
  }

  mergedData.posts.edges = mergedEdges
  if (paginationConfig.didInjectPageInfo) {
    delete mergedData.posts.pageInfo
  }

  return mergedData
}

export async function getPreviewPost(id, idType = 'DATABASE_ID') {
  const data = await fetchAPI(
    `
    query PreviewPost($id: ID!, $idType: PostIdType!) {
      post(id: $id, idType: $idType) {
        databaseId
        slug
        status
      }
    }`,
    {
      variables: { id, idType },
      requireAuth: true,
    }
  )
  return data.post
}

export async function getAllPostsWithSlug() {
  return memoizeDuringBuild('getAllPostsWithSlug', async () => {
    const data = await fetchAPI(`
    {
      posts(first: 1000) {
        edges {
          node {
            slug
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `)
    return data?.posts
  })
}
export async function getFirstFewPosts() {
  const data = await fetchAPI(
    `
    query posts {
      posts(first: 3, where: { orderby: { field: DATE, order: DESC } }) {
        edges {
          node {
            title
            slug
            uri
            databaseId
            seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
              author {
              node {
                name
                firstName
                lastName
                slug
                avatar { url }
                seo {
                social {
                  linkedIn
                  facebook
                  twitter
                }
            }
              }
            }
            categories {
                nodes {
                  name
                  id
                  databaseId
                  slug
                }
            }
          }
        }
      }
       allWhitepaper(first: 1, where: { orderby: { field: DATE, order: DESC } }) {
        edges {
          node{
            id
            uri
            title
          }
        }
      }
    }
  `)

  return data
}

export async function getAllPostsForHome(preview, locale = 'en') {
  // Use lowercase locale string as the GraphQL schema expects string, not enum
  const language = (locale || 'en').toLowerCase();

  return memoizeDuringBuild(`getAllPostsForHome:${language}:${Boolean(preview)}`, async () => {
  wpFetchDebug('Fetching posts for WP language:', language);

  const query = `
    query AllPosts {
      posts(
        first: 1000
        where: {
          language: "${language}"
          orderby: { field: DATE, order: DESC }
        }
      ) {
        edges {
          node {
            title
            excerpt
            slug
            date
            modified
            languageCode
            subTitle { subTitle }
            parentSlug { parentSlug }
            featuredImage { node { sourceUrl } }
            author {
              node {
                name
                firstName
                lastName
                slug
                avatar { url }
                seo {
                social {
                  linkedIn
                  facebook
                  twitter
                }
            }
              }
            }
            databaseId
            seo {
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
            categories {
              nodes {
                name
                id
                databaseId
                slug
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  try {
    const data = await fetchAPI(query, {
      variables: { language },
      locale: locale,
    });

    const posts = data?.posts || { edges: [] };
    posts.edges = (posts.edges || []).filter((edge: any) => Boolean(edge?.node?.slug));
    const postCount = posts.edges?.length || 0;

    // Verify posts are in the correct language (safety check)
    if (postCount > 0 && language !== 'en') {
      const firstPostLanguage = posts.edges[0]?.node?.languageCode?.toLowerCase();
      if (firstPostLanguage && firstPostLanguage !== language) {
        console.warn(`Warning: First post language is "${firstPostLanguage}" but requested "${language}". This might indicate a language filter issue.`);
      }
    }

    wpFetchDebug(`Posts fetched successfully for locale "${language}":`, postCount, 'posts');

    // If we got posts (even if 0), return them - don't fallback
    return posts;
  } catch (error: any) {
    const errorMessage = error.message || '';

    // Check if this is specifically a GraphQL "Internal server error" 
    // This typically means no posts exist for that language
    const isInternalServerError = errorMessage.includes('Internal server error');

    wpFetchDebug(`Error for locale "${language}":`, errorMessage);
    wpFetchDebug(`Is internal server error:`, isInternalServerError);

    // ONLY fallback if:
    // 1. It's NOT English (we don't want to fallback English to English)
    // 2. It's specifically an "Internal server error" (indicates no posts for that language)
    if (language !== 'en' && isInternalServerError) {
      console.warn(`Internal server error for locale "${language}" (no posts available), falling back to English`);

      try {
        const fallbackQuery = query.replace(`language: "${language}"`, 'language: "en"');
        const fallbackData = await fetchAPI(fallbackQuery, {
          variables: { language: 'en' },
          locale: 'en',
        });

        wpFetchDebug(`Fallback to English successful:`, fallbackData?.posts?.edges?.length || 0, 'posts');
        return fallbackData?.posts || { edges: [] };
      } catch (fallbackError) {
        console.error('Failed to fetch posts even with English fallback:', fallbackError);
        return { edges: [] };
      }
    } else {
      // For other errors (502, network errors, etc.) or if already English:
      // - If English fails, return empty to prevent build failure
      // - If other language fails with non-internal error, throw to see what's wrong
      console.error(`Error fetching posts for locale "${language}":`, errorMessage);

      if (language === 'en') {
        console.warn('Returning empty posts array for English to prevent build failure');
        return { edges: [] };
      }

      // Re-throw non-internal errors so we can see what's actually wrong
      // This helps debug issues like 502 errors, network problems, etc.
      throw error;
    }
  }
  });
}

export async function getAllPostsByDatabaseID(postIds: number[] | null, locale = 'en') {
  // Use lowercase locale string as the GraphQL schema expects string, not enum
  let idsString = "0";
  if (Array.isArray(postIds) && postIds.length > 0) {
    idsString = postIds.join(", ");
  }
  const language = (locale || 'en').toLowerCase();
  const query = `
    query AllPosts {
      posts(
        first: 50
        where: {
          language: "${language}"
          orderby: { field: DATE, order: DESC }
          in: [${idsString}]
        }
      ) {
        edges {
          node {
            title
            excerpt
            content
            slug
            date
            modified
            languageCode
            subTitle { subTitle }
            parentSlug { parentSlug }
            featuredImage { node { sourceUrl } }
            author {
              node {
                name
                firstName
                lastName
                slug
                avatar { url }
                seo {
                  social {
                    linkedIn
                    facebook
                    twitter
                  }
                }
              }
            }
            databaseId
            seo {
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
            categories {
              nodes {
                name
                id
                databaseId
                slug
              }
            }
          }
        }
      }
    }
  `;

  try {
    const data = await fetchAPI(query, {
      variables: { language },
      locale: locale,
    });

    const posts = data?.posts || { edges: [] };
    const postCount = posts.edges?.length || 0;

    // Verify posts are in the correct language (safety check)
    if (postCount > 0 && language !== 'en') {
      const firstPostLanguage = posts.edges[0]?.node?.languageCode?.toLowerCase();
      if (firstPostLanguage && firstPostLanguage !== language) {
        console.warn(`Warning: First post language is "${firstPostLanguage}" but requested "${language}". This might indicate a language filter issue.`);
      }
    }

    wpFetchDebug(`Posts fetched successfully for locale "${language}":`, postCount, 'posts');

    // If we got posts (even if 0), return them - don't fallback
    return posts;
  } catch (error: any) {
    const errorMessage = error.message || '';

    // Check if this is specifically a GraphQL "Internal server error" 
    // This typically means no posts exist for that language
    const isInternalServerError = errorMessage.includes('Internal server error');

    wpFetchDebug(`Error for locale "${language}":`, errorMessage);
    wpFetchDebug(`Is internal server error:`, isInternalServerError);

    // ONLY fallback if:
    // 1. It's NOT English (we don't want to fallback English to English)
    // 2. It's specifically an "Internal server error" (indicates no posts for that language)
    if (language !== 'en' && isInternalServerError) {
      console.warn(`Internal server error for locale "${language}" (no posts available), falling back to English`);

      try {
        const fallbackQuery = query.replace(`language: "${language}"`, 'language: "en"');
        const fallbackData = await fetchAPI(fallbackQuery, {
          variables: { language: 'en' },
          locale: 'en',
        });

        wpFetchDebug(`Fallback to English successful:`, fallbackData?.posts?.edges?.length || 0, 'posts');
        return fallbackData?.posts || { edges: [] };
      } catch (fallbackError) {
        console.error('Failed to fetch posts even with English fallback:', fallbackError);
        return { edges: [] };
      }
    } else {
      // For other errors (502, network errors, etc.) or if already English:
      // - If English fails, return empty to prevent build failure
      // - If other language fails with non-internal error, throw to see what's wrong
      console.error(`Error fetching posts for locale "${language}":`, errorMessage);

      if (language === 'en') {
        console.warn('Returning empty posts array for English to prevent build failure');
        return { edges: [] };
      }

      // Re-throw non-internal errors so we can see what's actually wrong
      // This helps debug issues like 502 errors, network problems, etc.
      throw error;
    }
  }
}


export async function getPostAndMorePosts(slug, preview, previewData, locale = 'en') {
  const language = (locale || 'en').toLowerCase();
  wpFetchDebug('Fetching post by slug for locale:', language, 'slug:', slug);

  const postPreview = preview && previewData?.post
  // The slug may be the id of an unpublished post
  const isId = Number.isInteger(Number(slug))
  const isSamePost = isId
    ? Number(slug) === postPreview.id
    : slug === postPreview.slug
  const isDraft = isSamePost && postPreview?.status === 'draft'
  const isRevision = isSamePost && postPreview?.status === 'publish'

  const fragmentQuery = `
    fragment AuthorFields on User {
      name
      firstName
      lastName
      avatar {
        url
      }
      seo {
          social {
            linkedIn
            facebook
            twitter
          }
        }
      description
    }
    fragment PostFields on Post {
      title
      excerpt
      slug
      date
      modified
      languageCode
      subTitle {
          subTitle
        }
      featuredImage {
        node {
          sourceUrl
        }
      }
      author {
        node {
        slug
          ...AuthorFields
        }
      }
      seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
            
      categories {
        edges {
          node {
            name
            slug
          }
        }
      }
      tags {
        edges {
          node {
            name
          }
        }
      }
    }
  `;

  // Query to fetch post by slug (fast, single post query)
  const queryBySlug = `
    ${fragmentQuery}
    query PostBySlug($id: ID!, $idType: PostIdType!) {
      post(id: $id, idType: $idType) {
        ...PostFields
        content
        ${isRevision
      ? `
        revisions(first: 1, where: { orderby: { field: MODIFIED, order: DESC } }) {
          edges {
            node {
              title
              excerpt
              content
              author {
                node {
                  ...AuthorFields
                }
              }
            }
          }
        }
        `
      : ''
    }
      }
      posts(first: 3, where: { language: "${language}", orderby: { field: DATE, order: DESC } }) {
        edges {
          node {
            ...PostFields
          }
        }
      }
    }
  `;

  try {
    // First, try fetching by slug directly (fast, single post query)
    wpFetchDebug(`Fetching post by slug directly...`);
    let slugData = await fetchAPI(
      queryBySlug,
      {
        variables: {
          id: isDraft ? postPreview.id : slug,
          idType: isDraft ? 'DATABASE_ID' : 'SLUG',
        },
        locale: locale,
        requireAuth: Boolean(preview),
      }
    );

    // Check if the fetched post matches the requested language
    let postLanguage = slugData?.post?.languageCode?.toLowerCase();
    if (postLanguage === language) {
      // Language matches, use this post
      wpFetchDebug(`Post found by slug with matching language "${language}"`);

      // Draft posts may not have an slug
      if (isDraft && slugData.post) slugData.post.slug = postPreview.id
      // Apply a revision (changes in a published post)
      if (isRevision && slugData.post?.revisions) {
        const revision = slugData.post.revisions.edges[0]?.node
        if (revision) Object.assign(slugData.post, revision)
        delete slugData.post.revisions
      }

      // Filter out the main post from related posts
      if (slugData.posts?.edges) {
        slugData.posts.edges = slugData.posts.edges.filter(({ node }) => node.slug !== slug)
        // If there are still 3 posts, remove the last one
        if (slugData.posts.edges.length > 2) slugData.posts.edges.pop()
      }

      return slugData;
    } else {
      // Post language doesn't match requested language
      console.warn(`Post found but language is "${postLanguage}", not "${language}". Attempting to find ${language} translation...`);

      // If requesting non-English but post is English, try to find translated version
      // Only do this if language doesn't match - avoids unnecessary queries
      if (language !== 'en' && postLanguage === 'en') {
        // Try with a very small limit first to avoid 502 errors
        const searchLimits = [10, 20, 50];
        let foundTranslation = false;

        for (const limit of searchLimits) {
          try {
            wpFetchDebug(`Searching for ${language} translation (limit: ${limit})...`);
            const translationQuery = `
              ${fragmentQuery}
              query FindTranslation {
                posts(first: ${limit}, where: { language: "${language}", orderby: { field: DATE, order: DESC } }) {
                  edges {
                    node {
                      ...PostFields
                      content
                      parentSlug {
                        parentSlug
                      }
                    }
                  }
                }
              }
            `;

            const translationData = await fetchAPI(translationQuery, { locale: locale });

            if (translationData?.posts?.edges?.length > 0) {
              // First, try to find by matching slug (most reliable)
              let translatedPost = translationData.posts.edges.find(
                ({ node }) => node.slug === slug
              );

              // If not found by slug, try to find by matching parentSlug
              if (!translatedPost && slugData.post?.parentSlug?.parentSlug) {
                const englishParentSlug = slugData.post.parentSlug.parentSlug;
                translatedPost = translationData.posts.edges.find(
                  ({ node }) => node.parentSlug?.parentSlug === englishParentSlug
                );
              }

              // If still not found, try to find by similar date and category
              if (!translatedPost && slugData.post?.categories?.edges?.length > 0) {
                const englishCategorySlug = slugData.post.categories.edges[0]?.node?.slug;
                const englishDate = slugData.post.date;

                if (englishCategorySlug) {
                  const sameCategoryPosts = translationData.posts.edges.filter(
                    ({ node }) => node.categories?.edges?.some(
                      (cat) => cat.node.slug === englishCategorySlug
                    )
                  );

                  if (sameCategoryPosts.length > 0) {
                    // Try to match by similar date (within 7 days)
                    translatedPost = sameCategoryPosts.find(
                      ({ node }) => node.date && Math.abs(new Date(node.date).getTime() - new Date(englishDate).getTime()) < 7 * 24 * 60 * 60 * 1000
                    ) || sameCategoryPosts[0];
                  }
                }
              }

              if (translatedPost) {
                wpFetchDebug(`✅ Found ${language} translation of the post!`);

                // Use the translated post
                const translatedData = {
                  post: translatedPost.node,
                  posts: {
                    edges: translationData.posts.edges
                      .filter(({ node }) => node.slug !== translatedPost.node.slug)
                      .slice(0, 3)
                  }
                };

                if (isDraft && translatedData.post) translatedData.post.slug = postPreview.id
                if (isRevision && translatedData.post?.revisions) {
                  const revision = translatedData.post.revisions.edges[0]?.node
                  if (revision) Object.assign(translatedData.post, revision)
                  delete translatedData.post.revisions
                }

                if (translatedData.posts?.edges) {
                  translatedData.posts.edges = translatedData.posts.edges.filter(({ node }) => node.slug !== translatedPost.node.slug)
                  if (translatedData.posts.edges.length > 2) translatedData.posts.edges.pop()
                }

                foundTranslation = true;
                return translatedData;
              } else {
                wpFetchDebug(`⚠️ No ${language} translation found in ${translationData.posts.edges.length} posts (limit: ${limit})`);
                // Try next limit if available
                if (limit < searchLimits[searchLimits.length - 1]) {
                  continue;
                }
              }
            }
          } catch (translationError) {
            const is502Error = translationError.message?.includes('502') || translationError.message?.includes('Bad Gateway');
            if (is502Error) {
              console.warn(`❌ 502 error searching for ${language} translation with limit ${limit}, stopping search`);
              break; // Don't try larger limits if we get 502
            } else {
              console.warn(`⚠️ Error searching for ${language} translation with limit ${limit}:`, translationError.message);
              // Try next limit for other errors
              continue;
            }
          }
        }

        if (!foundTranslation) {
          console.warn(`⚠️ Could not find ${language} translation after trying all limits`);
        }

        // Fallback: Use the English post but log that it's not in the requested language
        if (isDraft && slugData.post) slugData.post.slug = postPreview.id
        if (isRevision && slugData.post?.revisions) {
          const revision = slugData.post.revisions.edges[0]?.node
          if (revision) Object.assign(slugData.post, revision)
          delete slugData.post.revisions
        }

        if (slugData.posts?.edges) {
          slugData.posts.edges = slugData.posts.edges.filter(({ node }) => node.slug !== slug)
          if (slugData.posts.edges.length > 2) slugData.posts.edges.pop()
        }

        console.warn(`Using English post as ${language} translation not found. Post slug: ${slug}`);
        return slugData;
      } else {
        // For other cases, use the post as-is
        if (isDraft && slugData.post) slugData.post.slug = postPreview.id
        if (isRevision && slugData.post?.revisions) {
          const revision = slugData.post.revisions.edges[0]?.node
          if (revision) Object.assign(slugData.post, revision)
          delete slugData.post.revisions
        }

        if (slugData.posts?.edges) {
          slugData.posts.edges = slugData.posts.edges.filter(({ node }) => node.slug !== slug)
          if (slugData.posts.edges.length > 2) slugData.posts.edges.pop()
        }

        return slugData;
      }
    }
  } catch (error: any) {
    const errorMessage = error.message || '';
    const isInternalServerError = errorMessage.includes('Internal server error');
    const isLanguageMismatch = errorMessage.includes('language mismatch');

    wpFetchDebug(`Error fetching post for locale "${language}":`, errorMessage);

    // If post not found or language mismatch for non-English locale, try English fallback
    if (language !== 'en' && (isInternalServerError || isLanguageMismatch)) {
      console.warn(`Post not found or language mismatch for locale "${language}", trying English fallback`);

      try {
        const fallbackQuery = queryBySlug.replace(`language: "${language}"`, 'language: "en"');
        const fallbackData = await fetchAPI(
          fallbackQuery,
          {
            variables: {
              id: isDraft ? postPreview.id : slug,
              idType: isDraft ? 'DATABASE_ID' : 'SLUG',
            },
            locale: 'en',
          }
        );

        // Draft posts may not have an slug
        if (isDraft && fallbackData.post) fallbackData.post.slug = postPreview.id
        // Apply a revision
        if (isRevision && fallbackData.post?.revisions) {
          const revision = fallbackData.post.revisions.edges[0]?.node
          if (revision) Object.assign(fallbackData.post, revision)
          delete fallbackData.post.revisions
        }

        // Filter out the main post
        if (fallbackData.posts?.edges) {
          fallbackData.posts.edges = fallbackData.posts.edges.filter(({ node }) => node.slug !== slug)
          if (fallbackData.posts.edges.length > 2) fallbackData.posts.edges.pop()
        }

        wpFetchDebug(`Fallback to English successful for post`);
        return fallbackData;
      } catch (fallbackError) {
        console.error('Failed to fetch post even with English fallback:', fallbackError);
        // Return empty structure to prevent build failure
        return {
          post: null,
          posts: { edges: [] }
        };
      }
    } else {
      // For English or other errors, return empty structure
      console.error(`Error fetching post for locale "${language}":`, errorMessage);
      return {
        post: null,
        posts: { edges: [] }
      };
    }
  }
}


export async function getAllPagesWithSlug() {
  const data = await fetchAPI(`
   {
      pages(first: 200) {
        edges {
          node {
            id
            slug
            title
            uri
            isFrontPage
            parent {
              node {
                slug
              }
            }
            featuredImage {
              node {
                sourceUrl
              }
            }
            seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
            }
          }
        }
      }
    }
  `
  )
  return data?.pages
}
export async function getHomePagesWithSlug() {
  const data = await fetchAPI(`
   {
  pages(where: {id: 5}) {
    edges {
      node {
        id
        slug
        title
        uri
        content
        isFrontPage
        parent {
          node {
            slug
          }
        }
        featuredImage {
          node {
            sourceUrl
          }
        }
        seo {
          metaRobotsNoindex
          metaRobotsNofollow
          metaKeywords
          metaDesc
          title
        }
      }
    }
  }
}
  `
  )
  return data?.pages
}
export async function getPageForSlug(slug: string | string[] | undefined, parentPage: string = "", subParentPage: string = "", locale: string = 'en') {
  const slugStr = Array.isArray(slug) ? slug[0] : (slug || '');
  const language = (locale || 'en').toLowerCase();

  const data = await fetchAPI(`
     {
        pages(first: 200, where: { language: "${language}" }) {
          edges {
            node {
              id
              slug
              title
              uri
              content
              languageCode
              isFrontPage
              parent {
                node {
                  slug
                }
              }
              featuredImage {
                node {
                  sourceUrl
                }
              }
              seo{
                metaRobotsNoindex
                metaRobotsNofollow
                metaKeywords
                metaDesc
                title
              }
            }
          }
        }
      }
    `,
    {
      locale: locale,
    }
  )



  // Filter out the main page

  let uri = `/${parentPage}/${slugStr}/`
  if (subParentPage.length > 1) {
    uri = `/${parentPage}/${subParentPage}/${slugStr}/`
  }
  if (parentPage.length > 0) {

    data.pages.edges = data.pages.edges.filter(({ node }) => {
      return node.uri == `${uri}`
    })
  } else {
    data.pages.edges = data.pages.edges.filter(({ node }) => {
      return node.slug == `${slugStr}`
    })
  }


  return data
}

export async function getGeneralSettings() {
  const data = await fetchAPI(`
   {
      allSettings {
        generalSettingsTitle
        generalSettingsDescription
      }
    }
  `
  )
  return data
}

export async function getPostsByCategory(categoryName) {
  const data = await fetchAPI(
    `
    query AllPosts {
      posts(where: { categoryName: 'Blog' }, first: 6, orderby: { field: DATE, order: DESC }) {
        edges {
          node {
            title
            excerpt
            slug
            date
            featuredImage {
              node {
                sourceUrl
              }
            }
            seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
            }
            author {
              node {
                name
                firstName
                lastName
                slug
                avatar {
                  url
                }
              }
            }
            databaseId
          }
        }
      }
    }
  `
  )


  return data



}
export async function getCategoriesFivePostWithSlug() {
  return memoizeDuringBuild('getCategoriesFivePostWithSlug', async () => {
  const data = await fetchAPI(`
  query AllCat {
    categories(first: 50) {
    edges {
      node {
        name
        databaseId
        slug
         posts (first: 5){
          nodes {
            title
            excerpt
            slug
            date
            parentSlug {
              parentSlug
            }
            author {
            node {
              name
              firstName
              lastName
              slug
                avatar {
                  url
                }
              }
            }
            featuredImage {
              node {
                sourceUrl
              }
            }
            
          }
        }
      }
    }
  }
  }
  `)
  return data?.categories
  })
}
export async function getCategoriesFivePostWithSlugByID(postIds: number[] | null) {
  let idsString = "0";
  if (Array.isArray(postIds) && postIds.length > 0) {
    idsString = postIds.join(", ");
  }

  const data = await fetchAPI(`
    query AllCat {
      posts(where: {in: [${idsString}]}) {
        nodes {
          title
          excerpt
          slug
          date
          parentSlug {
            parentSlug
          }
          featuredImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
  `);
  return data?.posts;
}
export async function getAllCategoriesWithSlug() {
  const data = await fetchAPI(`
  query AllCat {
    categories(first: 50) {
    edges {
      node {
        name
        databaseId
        slug
         posts (first: 50){
          nodes {
            title
            excerpt
            slug
            date
            featuredImage {
              node {
                sourceUrl
              }
            }
            
          }
        }
      }
    }
  }
  }
  `)
  return data?.categories

}
export async function getAllAuthorWithSlug() {
  const data = await fetchAPI(`
  query AllCat {
      users(first: 100) {
    edges {
      node {
        name
        slug
        description
        avatar {
          url
        }
        posts {
          nodes {
            title
            date
            parentSlug {
              parentSlug
            }
            categories {
              nodes {
                name
                link
              }
            }
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
          }
        }
      }
    }
  }
  }
  `)
  return data?.users

}
export async function getCategoryPostsForSlugLimited(slug) {
  const data = await fetchAPI(`
  query AllCat {
   categories {
    edges {
      node {
        name
        databaseId
        slug
        uri
        description
        posts(first: 12) {
          nodes {
            title
            excerpt
            slug
            date
            parentSlug {
              parentSlug
            }
            featuredImage {
              node {
                sourceUrl
              }
            }
            
          }
        }
      }
    }
  }
  }
  `
  )
  // Filter out the main page
  data.categories.edges = data.categories.edges.filter(({ node }) => {
    return node.slug == `${slug}`
  })


  return data
}
export async function getAuthorPostsForSlugLimited(slug) {
  const data = await fetchAPI(`
  query AllCat {
     users(first: 50) {
    edges {
      node {
        name
        slug
        description
        avatar {
          url
        }
        posts {
          nodes {
            title
            date
            slug
            parentSlug {
              parentSlug
            }
            categories {
              nodes {
                name
                link
                slug
              }
            }
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
          }
        }
      }
    }
  }
  }
  `
  )

  data.users.edges = data.users?.edges.filter(({ node }) => {
    return node.slug == `${slug}`
  })

  return data
}

export async function getCategoryPostsForSlug(slug) {
  const data = await fetchAPI(`
  query AllCat {
   categories {
    edges {
      node {
        name
        databaseId
        slug
        description
        seo{
          metaRobotsNoindex
          metaRobotsNofollow
          metaKeywords
          metaDesc
          title
        }
        posts(first: 150) {
          nodes {
            title
            excerpt
            slug
            date
            subTitle {
              subTitle
            }
            author {
              node {
                name
                slug
              }
            }
            parentSlug {
              parentSlug
            }
            featuredImage {
              node {
                sourceUrl
              }
            }
            
          }
        }
      }
    }
  }
  }
  `
  )
  // Filter out the main page
  data.categories.edges = data.categories.edges.filter(({ node }) => {
    return node.slug == `${slug}`
  })


  return data
}

export async function getTagPostsForSlug(slug: string | undefined) {
  if (!slug) return null

  const tagPostsSelection = `
        name
        slug
        description
        seo{
          metaRobotsNoindex
          metaRobotsNofollow
          metaKeywords
          metaDesc
          title
        }
        posts(first: 150) {
          nodes {
            title
            excerpt
            slug
            date
            subTitle {
              subTitle
            }
            author {
              node {
                name
                slug
              }
            }
            parentSlug {
              parentSlug
            }
            featuredImage {
              node {
                sourceUrl
              }
            }
            categories {
              nodes {
                name
                slug
              }
            }
          }
        }
  `

  // Topics pages expect the legacy categories.edges shape.
  const normalize = (node: any | null | undefined) => {
    if (!node) return null
    return {
      categories: {
        edges: [{ node }],
      },
    }
  }

  // Prefer single-tag lookup — tags(where: { slug }) is unreliable on this WPGraphQL schema.
  try {
    const byId = await fetchAPI(
      `
      query TagBySlug($id: ID!) {
        tag(id: $id, idType: SLUG) {
          ${tagPostsSelection}
        }
      }
    `,
      { variables: { id: slug } },
    )
    if (byId?.tag) {
      return normalize(byId.tag)
    }
  } catch (e) {
    console.warn('getTagPostsForSlug: tag(idType: SLUG) failed, trying list filter', e)
  }

  try {
    const byWhere = await fetchAPI(
      `
      query TagByWhereSlug($slug: [String]!) {
        tags(first: 1, where: { slug: $slug }) {
          edges {
            node {
              ${tagPostsSelection}
            }
          }
        }
      }
    `,
      { variables: { slug: [slug] } },
    )
    const node = byWhere?.tags?.edges?.[0]?.node
    if (node) {
      return normalize(node)
    }
  } catch (e) {
    console.warn('getTagPostsForSlug: tags(where: { slug }) failed, trying full list', e)
  }

  const data = await fetchAPI(
    `
  query AllTagsForArchive {
    tags(first: 200) {
      edges {
        node {
          ${tagPostsSelection}
        }
      }
    }
  }
  `,
  )

  const node = data?.tags?.edges?.find(
    ({ node: tagNode }) => tagNode.slug === slug
  )?.node
  return normalize(node)
}

export async function getAllTagsWithSlug() {
  return memoizeDuringBuild('getAllTagsWithSlug', async () => {
  const data = await fetchAPI(`
    query AllTags {
      tags(first: 100) {
        edges {
          node {
            name
            slug
          }
        }
      }
    }
  `)
  return data?.tags
  })
}

export async function getAllResources() {
  const data = await fetchAPI(`
   {
      allWhitepaper {
        edges {
          node{
            id
            uri
            title
            excerpt
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
          allEbook {
        edges {
          node{
            id
            uri
            title
            excerpt
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
      allGuide {
        edges {
          node{
            id
            uri
            title
            excerpt
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
      allFactsheet {
        edges {
          node{
            id
            uri
            title
            excerpt
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
    }
  `
  )
  return data
}
export async function getAllWhitepaper() {
  const data = await fetchAPI(`
   {
      allWhitepaper {
        edges {
          node{
            id
            uri
            title
            excerpt
            slug
            date
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
    }
  `
  )
  return data?.allWhitepaper
}
export async function getAllCareers() {
  const data = await fetchAPI(`
   {
      allCareers {
        edges {
          node{
            id
            uri
            title
            excerpt
            slug
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
    }
  `
  )
  return data?.allCareers
}

export async function getWhitepaperForSlug(slug, parentPage) {
  const data = await fetchAPI(`
   {
      allWhitepaper {
        edges {
          node{
            id
            uri
            title
            excerpt
            slug
            content
           acf_resources {
              hubspotFormId
              pdfUrl
              sliderImages{
                  bookImage1
                  bookImage2
                  bookImage3
                  altTagImage
              }
              aboutBook{
                  title1
                  subtitle
                  listPoint {
                    point1
                    point2
                    point3
                    point4
                    point5
                    point6
                  }
              }
            }
            seo {
                metaDesc
                metaKeywords
                title
                twitterTitle
                opengraphTitle
                canonical
                metaRobotsNofollow
                metaRobotsNoindex
                opengraphAuthor
                opengraphDescription
                opengraphModifiedTime
                opengraphPublishedTime
                opengraphPublisher
                opengraphUrl
                opengraphType
                readingTime
                twitterDescription
          }
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
    }
  `
  )
  let uri = `/${parentPage}/${slug}`
  if (parentPage.length > 0) {
    data.allWhitepaper.edges = data.allWhitepaper.edges.filter(({ node }) => {
      return node.uri == `${uri}/`
    })
  } else {
    data.allWhitepaper.edges = data.allWhitepaper.edges.filter(({ node }) => {
      return node.slug == `${slug}`
    })
  }
  // Filter out the main page

  return data
}

export async function getCareersForSlug(slug, parentPage) {
  const data = await fetchAPI(`
   {
      allCareers {
        edges {
          node{
            id
            title
            excerpt
            slug
            content
            seo {
                metaDesc
                metaKeywords
                title
                twitterTitle
                opengraphTitle
                canonical
                metaRobotsNofollow
                metaRobotsNoindex
                opengraphAuthor
                opengraphDescription
                opengraphModifiedTime
                opengraphPublishedTime
                opengraphPublisher
                opengraphUrl
                opengraphType
                readingTime
                twitterDescription
          }
             featuredImage {
                node {
                  altText
                  sourceUrl
                  title
                }
             }
          }
        }
      }
    }
  `
  )
  let uri = `/${parentPage}/${slug}`
  if (parentPage.length > 0) {
    data.allCareers.edges = data.allCareers.edges.filter(({ node }) => {
      return node.uri == `${uri}/`
    })
  } else {
    data.allCareers.edges = data.allCareers.edges.filter(({ node }) => {
      return node.slug == `${slug}`
    })
  }
  // Filter out the main page

  return data
}

export async function getAllIntegrationsByCategory() {
  const data = await fetchAPI(`
   {
  allIntegrationCategories {
    edges {
      node {
        link
        name
        slug
        integration(first: 50) {
          edges {
            node {
              acf_integrations {
                knowMoreLink
                integrationImage {
                  altText
                  sourceUrl
                }
              }
              slug
              title
            }
          }
        }
      }
    }
  }
}
  `
  )
  return data
}





export async function getPost(slug) {
  const data = await fetchAPI(`
   query AllPosts {
      posts( where: { orderby: { field: DATE, order: DESC } }) {
        edges {
          node {
            title
            excerpt
            slug
            date
            uri
            content
            subTitle {
              subTitle
            }
            featuredImage {
              node {
                sourceUrl
              }
            }
            author {
              node {
                name
                firstName
                lastName
                slug
                avatar {
                  url
                }
              }
            }
            databaseId
            seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
            categories {
                nodes {
                  name
                  id
                  databaseId
                  slug
                  uri
                }
            }
          }
        }
      }
    }
  `
  )
  // Filter out the main page

  data.posts.edges = data.posts.edges.filter(({ node }) => {
    return node.slug == `${slug}`
  })

  return data
}

export async function getProductUpdatesPosts() {
  const data = await fetchAPI(`
   {
      productUpdates(first: 200) {
      edges {
        node {
          title
          excerpt
          slug
          uri
          seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
          featuredImage {
            node {
              altText
              sourceUrl
            }
          }
          acf_productUpdate {
            publishDate
          }
        }
      }
    }
  }
  `);

  return data.productUpdates;
}

export async function getProductUpdatesPostsBySlug(slug, parentPage) {
  const data = await fetchAPI(`
    {
      productUpdates(first: 100) {
      edges {
        node {
          title
          excerpt
          slug
          uri
          content
          date
          modified
          seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
          featuredImage {
            node {
              altText
              sourceUrl
            }
          }
          author {
            node {
              name
              firstName
              lastName
              slug
              username
              description
              avatar { url }
              seo {
                social {
                  linkedIn
                  facebook
                  twitter
                }
              }
            }
          }
          acf_productUpdate {
           publishDate
          }
        }
      }
    }
  }
  `
  )
  let uri = `/${parentPage}/${slug}`
  if (parentPage.length > 0) {
    data.productUpdates.edges = data.productUpdates.edges.filter(({ node }) => {
      return node.uri == `${uri}/`
    })
  } else {
    data.productUpdates.edges = data.productUpdates.edges.filter(({ node }) => {
      return node.slug == `${slug}`
    })
  }

  return data
}

export async function getAllPodcast() {
  const data = await fetchAPI(`
   {
      allPodcast(first: 100) {
        edges {
          node {
            excerpt
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
            slug
            title
            uri
            date
            seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
            podcast {
              fieldGroupName
              episode
              mediaUrl
              hostBy {
                designation
                name
                profileUrl
                profileImage {
                  altText
                  sourceUrl
                }
              }
              guest {
                designation
                name
                profileUrl
                profileImage {
                  altText
                  sourceUrl
                }
              }
            }
          }
        }
      }
  }
  `);

  return data.allPodcast;
}

export async function getAllPodcastBySlug(slug, parentPage) {
  const data = await fetchAPI(`
    {
      allPodcast {
        edges {
          node {
            excerpt
            content
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
            slug
            title
            uri
            date
            seo{
              metaRobotsNoindex
              metaRobotsNofollow
              metaKeywords
              metaDesc
              title
              readingTime
            }
            podcast {
              fieldGroupName
              episode
              mediaUrl
              hostBy {
                designation
                name
                profileUrl
                profileImage {
                  altText
                  sourceUrl
                }
              }
              guest {
                designation
                name
                profileUrl
                profileImage {
                  altText
                  sourceUrl
                }
              }
            }
          }
        }
      }
  }
  `
  )
  let uri = `/${parentPage}/${slug}`
  if (parentPage.length > 0) {
    data.allPodcast.edges = data.allPodcast.edges.filter(({ node }) => {
      return node.uri == `${uri}/`
    })
  } else {
    data.allPodcast.edges = data.allPodcast.edges.filter(({ node }) => {
      return node.slug == `${slug}`
    })
  }

  return data
}

function parseWebinarDateTimeToTimestamp(node: any) {
  const rawDateTime = node?.acf_webinars?.dateTime;
  const dateTime = typeof rawDateTime === "string" ? rawDateTime.trim() : "";

  if (dateTime) {
    const directParsed = Date.parse(dateTime);
    if (!Number.isNaN(directParsed)) return directParsed;

    const dateTimeIsoParsed = Date.parse(dateTime.replace(" ", "T"));
    if (!Number.isNaN(dateTimeIsoParsed)) return dateTimeIsoParsed;

    const dmyMatch = dateTime.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?)?$/
    );
    if (dmyMatch) {
      const [, day, month, year, hh = "0", mm = "0", meridiem] = dmyMatch;
      let hour = Number(hh);
      const minute = Number(mm);

      if (meridiem) {
        const isPm = meridiem.toLowerCase() === "pm";
        if (isPm && hour < 12) hour += 12;
        if (!isPm && hour === 12) hour = 0;
      }

      const parsed = new Date(Number(year), Number(month) - 1, Number(day), hour, minute).getTime();
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  const fallbackDate = typeof node?.date === "string" ? Date.parse(node.date) : NaN;
  return Number.isNaN(fallbackDate) ? Number.POSITIVE_INFINITY : fallbackDate;
}

export async function getAllWebinars() {
  try {
    const data = await fetchAPI(`
     {
        webinars(first: 50) {
          edges {
            node {
              id
              uri
              title
              slug
              excerpt
              date
              featuredImage {
                node {
                  altText
                  sourceUrl
                }
              }
              seo {
                metaRobotsNoindex
                metaRobotsNofollow
                metaKeywords
                metaDesc
                title
                readingTime
              }
              acf_webinars {
          dateTime
          fieldGroupName
          location
          speakers {
            fieldGroupName
            speakerName
            speakersProfile {
              altText
              sourceUrl
            }
            speakerdesignation  
            hostedBy
            hostedByImage {
              sourceUrl
              altText
            }
            hostedByLink
          }
          isRegisterFormEnable
        }
        tags {
          edges {
            node {
              name
            }
          }
        }
            }
          }
        }
      }
    `
    )
    const webinars = data?.webinars || { edges: [] };

    if (Array.isArray(webinars?.edges)) {
      webinars.edges = [...webinars.edges].sort((a, b) => {
        const firstTs = parseWebinarDateTimeToTimestamp(a?.node);
        const secondTs = parseWebinarDateTimeToTimestamp(b?.node);
        return firstTs - secondTs;
      });
    }

    return webinars;
  } catch (error: any) {
    console.error(`Error fetching webinars:`, error.message);
    return { edges: [] };
  }
}

export async function getAllWebinarsBySlug(slug, parentPage) {
  try {
    const data = await fetchAPI(`
     {
        webinars(first: 50) {
          edges {
            node {
              id
              uri
              title
              slug
              excerpt
              content
              date
              featuredImage {
                node {
                  altText
                  sourceUrl
                }
              }
              seo {
                metaDesc
                metaKeywords
                title
                twitterTitle
                opengraphTitle
                canonical
                metaRobotsNofollow
                metaRobotsNoindex
                opengraphAuthor
                opengraphDescription
                opengraphModifiedTime
                opengraphPublishedTime
                opengraphPublisher
                opengraphUrl
                opengraphType
                readingTime
                twitterDescription
              }
              acf_webinars {
          dateTime
          fieldGroupName
          location
          liveWebinarUrl
          webinarlink {
                  target
                  title
                  url
                }
          speakers {
            fieldGroupName
            speakerName
            speakersProfile {
              altText
              sourceUrl
            }
            speakerdesignation
            speakerName2nd
            speakerDesignation2nd
            speakerProfile2nd {
              altText
              sourceUrl
            }
            hostedBy
            hostedByImage {
              sourceUrl
              altText
            }
            hostedByLink
          }
          isRegisterFormEnable
        }
        tags {
          edges {
            node {
              name
            }
          }
        }
            }
          }
        }
      }
    `)

    let uri = `/${parentPage}/${slug}`
    if (parentPage.length > 0) {
      data.webinars.edges = data.webinars.edges.filter(({ node }) => {
        return node.uri == `${uri}/`
      })
    } else {
      data.webinars.edges = data.webinars.edges.filter(({ node }) => {
        return node.slug == `${slug}`
      })
    }

    return data
  } catch (error: any) {
    console.error(`Error fetching webinar for slug "${slug}":`, error.message);
    return { webinars: { edges: [] } };
  }
}